import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Plus, Edit3, Trash2, Power, Upload, X, CheckCircle2, XCircle, RefreshCw,
    ShieldCheck, FileBadge,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import LoadingState from "../components/LoadingState";
import EmptyState from "../components/EmptyState";
import DomainChipInput from "../components/DomainChipInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import api from "../lib/api";

const CA_SOURCE_LABEL = {
    agid_tsl: "AgID TSL",
    manual_upload: "Upload manuale",
    test_ca: "CA di test",
};

const EMPTY_FORM = {
    id: null,
    name: "",
    domains: [],
    is_active: true,
    trusted_ca_source: "agid_tsl",
    notes: "",
};

function fmtDT(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome" }); }
    catch { return iso; }
}

export default function AdminPecProvidersPage() {
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [certOpen, setCertOpen] = useState(null); // provider object
    const [uploadFile, setUploadFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/pec-providers");
            setProviders(data.items || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore di caricamento");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const openNew = () => { setForm(EMPTY_FORM); setModalOpen(true); };

    const openEdit = (p) => {
        setForm({
            id: p.id,
            name: p.name || "",
            domains: p.domains || [],
            is_active: !!p.is_active,
            trusted_ca_source: p.trusted_ca_source || "agid_tsl",
            notes: p.notes || "",
        });
        setModalOpen(true);
    };

    const save = async (ev) => {
        ev.preventDefault();
        if (!form.name.trim()) { toast.error("Nome gestore obbligatorio"); return; }
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                domains: form.domains,
                is_active: form.is_active,
                trusted_ca_source: form.trusted_ca_source,
                notes: form.notes,
            };
            if (form.id) {
                await api.patch(`/admin/pec-providers/${form.id}`, payload);
                toast.success("Gestore aggiornato");
            } else {
                await api.post("/admin/pec-providers", payload);
                toast.success("Gestore creato");
            }
            setModalOpen(false);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (p) => {
        try {
            await api.patch(`/admin/pec-providers/${p.id}`, { is_active: !p.is_active });
            toast.success(!p.is_active ? "Gestore attivato" : "Gestore disattivato");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        }
    };

    const softDelete = async (p) => {
        if (!window.confirm(
            `Eliminare il gestore "${p.name}"?\n\nL'eliminazione è soft-delete: il gestore verrà nascosto ma le PEC storiche manterranno il riferimento.`,
        )) return;
        try {
            await api.delete(`/admin/pec-providers/${p.id}`);
            toast.success("Gestore eliminato (soft delete)");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        }
    };

    const uploadCert = async () => {
        if (!uploadFile || !certOpen) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", uploadFile);
            const { data } = await api.post(
                `/admin/pec-providers/${certOpen.id}/upload-certificate`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            toast.success(`Certificato caricato · totale: ${data.total_certificates}`);
            setUploadFile(null);
            await load();
            // refresh open provider ref
            const refreshed = (await api.get("/admin/pec-providers")).data.items.find((x) => x.id === certOpen.id);
            setCertOpen(refreshed || null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Certificato non valido");
        } finally {
            setUploading(false);
        }
    };

    const removeCert = async (certId) => {
        if (!certOpen) return;
        if (!window.confirm("Rimuovere questo certificato manuale?")) return;
        try {
            await api.delete(`/admin/pec-providers/${certOpen.id}/certificates/${certId}`);
            toast.success("Certificato rimosso");
            const refreshed = (await api.get("/admin/pec-providers")).data.items.find((x) => x.id === certOpen.id);
            setCertOpen(refreshed || null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        }
    };

    if (loading) return <LoadingState label="Caricamento gestori PEC…" />;

    return (
        <div className="space-y-4" data-testid="admin-pec-providers-page">
            <PageHeader
                title="Gestori PEC"
                subtitle="Configurazione globale dei gestori PEC riconosciuti e dei loro domini."
                breadcrumbs={[{ label: "Amministrazione" }, { label: "Gestori PEC" }]}
                actions={
                    <div className="flex gap-2">
                        <button
                            onClick={load}
                            data-testid="providers-reload-btn"
                            className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg border border-[#DCE3EE] bg-white text-[#172033] hover:bg-[#F5F7FB]"
                        >
                            <RefreshCw className="w-4 h-4" /> Ricarica
                        </button>
                        <button
                            onClick={openNew}
                            data-testid="new-provider-btn"
                            className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-[#2563EB] text-white hover:bg-[#1D4ED8]"
                        >
                            <Plus className="w-4 h-4" /> Nuovo gestore
                        </button>
                    </div>
                }
            />

            {providers.length === 0 ? (
                <EmptyState
                    title="Nessun gestore configurato"
                    description="Crea il primo gestore PEC per abilitare il matching automatico dei mittenti."
                />
            ) : (
                <div className="bg-white border border-[#DCE3EE] rounded-xl overflow-hidden">
                    <table className="w-full text-sm" data-testid="providers-table">
                        <thead className="bg-[#F5F7FB] text-[11px] uppercase text-[#64748B]">
                            <tr>
                                <th className="text-left px-4 py-2.5">Nome</th>
                                <th className="text-left px-4 py-2.5">Slug</th>
                                <th className="text-left px-4 py-2.5">Domini</th>
                                <th className="text-left px-4 py-2.5">Cert. manuali</th>
                                <th className="text-left px-4 py-2.5">Fonte CA</th>
                                <th className="text-left px-4 py-2.5">Stato</th>
                                <th className="text-left px-4 py-2.5">Modificato</th>
                                <th className="text-right px-4 py-2.5">Azioni</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#DCE3EE]">
                            {providers.map((p) => (
                                <tr key={p.id} data-testid={`provider-row-${p.slug}`}>
                                    <td className="px-4 py-3 font-medium text-[#172033]">{p.name}</td>
                                    <td className="px-4 py-3 font-mono text-xs text-[#64748B]">{p.slug}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex flex-wrap gap-1 max-w-[280px]">
                                            {(p.domains || []).slice(0, 3).map((d) => (
                                                <span key={d} className="inline-block bg-slate-100 text-slate-700 border border-slate-200 rounded-full px-2 py-0.5 text-[10px] font-mono">
                                                    {d}
                                                </span>
                                            ))}
                                            {(p.domains || []).length > 3 && (
                                                <span className="text-[10px] text-[#64748B]">+{p.domains.length - 3}</span>
                                            )}
                                            {(!p.domains || p.domains.length === 0) && (
                                                <span className="text-[10px] text-[#64748B] italic">nessuno</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <button
                                            onClick={() => setCertOpen(p)}
                                            data-testid={`provider-certs-${p.slug}`}
                                            className="inline-flex items-center gap-1.5 text-xs text-[#2563EB] hover:underline"
                                        >
                                            <FileBadge className="w-3.5 h-3.5" />
                                            {(p.manual_certificates || []).length}
                                        </button>
                                    </td>
                                    <td className="px-4 py-3 text-xs">{CA_SOURCE_LABEL[p.trusted_ca_source] || p.trusted_ca_source}</td>
                                    <td className="px-4 py-3">
                                        {p.is_active ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-[#16A34A]/10 text-[#16A34A]">
                                                <CheckCircle2 className="w-3 h-3" /> Attivo
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-slate-100 text-slate-500">
                                                <XCircle className="w-3 h-3" /> Disattivo
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-[11px] text-[#64748B]">{fmtDT(p.updated_at)}</td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                        <button
                                            onClick={() => openEdit(p)}
                                            data-testid={`provider-edit-${p.slug}`}
                                            title="Modifica"
                                            className="p-1.5 rounded hover:bg-[#F5F7FB] text-[#64748B] hover:text-[#2563EB]"
                                        >
                                            <Edit3 className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => toggleActive(p)}
                                            data-testid={`provider-toggle-${p.slug}`}
                                            title={p.is_active ? "Disattiva" : "Attiva"}
                                            className="p-1.5 rounded hover:bg-[#F5F7FB] text-[#64748B] hover:text-[#F59E0B]"
                                        >
                                            <Power className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => softDelete(p)}
                                            data-testid={`provider-delete-${p.slug}`}
                                            title="Elimina"
                                            className="p-1.5 rounded hover:bg-[#DC2626]/10 text-[#64748B] hover:text-[#DC2626]"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Create/Edit modal */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="max-w-2xl" data-testid="provider-modal">
                    <DialogHeader>
                        <DialogTitle>{form.id ? "Modifica gestore PEC" : "Nuovo gestore PEC"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={save} className="space-y-4">
                        <div>
                            <Label htmlFor="p-name" className="text-xs">Nome gestore *</Label>
                            <Input
                                id="p-name"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="es. Aruba PEC S.p.A."
                                className="mt-1 h-10"
                                data-testid="provider-name-input"
                                required
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Domini PEC</Label>
                            <div className="mt-1">
                                <DomainChipInput
                                    value={form.domains}
                                    onChange={(v) => setForm({ ...form, domains: v })}
                                    testIdPrefix="provider-domain"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs">Fonte trust anchor</Label>
                                <Select
                                    value={form.trusted_ca_source}
                                    onValueChange={(v) => setForm({ ...form, trusted_ca_source: v })}
                                >
                                    <SelectTrigger className="mt-1 h-10" data-testid="provider-source-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="agid_tsl">AgID Trust List</SelectItem>
                                        <SelectItem value="manual_upload">Upload manuale</SelectItem>
                                        <SelectItem value="test_ca">CA di test</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-end gap-3 pb-1">
                                <Switch
                                    id="p-active"
                                    checked={form.is_active}
                                    onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                                    data-testid="provider-active-switch"
                                />
                                <Label htmlFor="p-active" className="text-sm cursor-pointer">
                                    {form.is_active ? "Attivo" : "Disattivato"}
                                </Label>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Note</Label>
                            <Textarea
                                value={form.notes}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                rows={2}
                                placeholder="Note opzionali (es. domini da verificare, riferimenti AgID…)"
                                data-testid="provider-notes-input"
                                className="mt-1"
                            />
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setModalOpen(false)}
                                data-testid="provider-cancel-btn"
                            >
                                Annulla
                            </Button>
                            <Button
                                type="submit"
                                disabled={saving}
                                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                                data-testid="provider-save-btn"
                            >
                                {saving ? "Salvataggio…" : (form.id ? "Aggiorna" : "Crea")}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Certificates modal */}
            <Dialog open={!!certOpen} onOpenChange={(v) => !v && setCertOpen(null)}>
                <DialogContent className="max-w-3xl" data-testid="provider-certs-modal">
                    <DialogHeader>
                        <DialogTitle>
                            <div className="flex items-center gap-2">
                                <ShieldCheck className="w-5 h-5 text-[#2563EB]" />
                                Certificati manuali · {certOpen?.name}
                            </div>
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="flex items-center gap-2">
                            <input
                                type="file"
                                accept=".pem,.crt,.cer,.der,application/x-pem-file"
                                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                                data-testid="provider-cert-file-input"
                                className="text-sm flex-1"
                            />
                            <Button
                                type="button"
                                onClick={uploadCert}
                                disabled={!uploadFile || uploading}
                                data-testid="provider-cert-upload-btn"
                                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                            >
                                <Upload className="w-4 h-4 mr-2" />
                                {uploading ? "Caricamento…" : "Carica"}
                            </Button>
                        </div>
                        <div className="text-[11px] text-[#64748B]">
                            Formati accettati: PEM o DER. Max 200 KB. Il certificato viene validato prima di essere salvato.
                        </div>
                        {(certOpen?.manual_certificates || []).length === 0 ? (
                            <div className="text-sm text-[#64748B] italic border border-dashed border-[#DCE3EE] rounded-lg p-6 text-center">
                                Nessun certificato manuale caricato.
                            </div>
                        ) : (
                            <div className="border border-[#DCE3EE] rounded-lg overflow-hidden">
                                <table className="w-full text-xs">
                                    <thead className="bg-[#F5F7FB] text-[10px] uppercase text-[#64748B]">
                                        <tr>
                                            <th className="text-left px-3 py-2">Subject</th>
                                            <th className="text-left px-3 py-2">Valido dal → al</th>
                                            <th className="text-left px-3 py-2">SHA-256</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-[#DCE3EE]">
                                        {certOpen.manual_certificates.map((c) => (
                                            <tr key={c.id} data-testid={`cert-row-${c.id}`}>
                                                <td className="px-3 py-2 truncate max-w-[300px]">{c.subject}</td>
                                                <td className="px-3 py-2 text-[10px] text-[#64748B]">
                                                    {fmtDT(c.not_before)} → {fmtDT(c.not_after)}
                                                </td>
                                                <td className="px-3 py-2 font-mono text-[10px] text-[#64748B]">
                                                    {c.fingerprint_sha256.slice(0, 16)}…
                                                </td>
                                                <td className="px-3 py-2 text-right">
                                                    <button
                                                        onClick={() => removeCert(c.id)}
                                                        data-testid={`cert-remove-${c.id}`}
                                                        className="p-1 rounded hover:bg-[#DC2626]/10 text-[#64748B] hover:text-[#DC2626]"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
